import { CircularProgress, Container } from '@mui/material';
import { ReactElement, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ConversionDialog from 'components/Modals/ConversionDialog';
import { HTTPError } from 'services/api/entities';
import { locations } from 'services/routes/locations';
import { Nullable } from 'types';
import {
  useReleaseNavigation,
  useCreateRelease,
} from 'services/hooks/distribution';
import useStyles from './style';

const SuccessSubscriptionModal = ({
  isLoading,
  userFetch,
  userFetchError,
  setSubscriptionActive,
  shouldBlockRedirect,
}: {
  isLoading: boolean;
  userFetch: () => void;
  userFetchError: Nullable<HTTPError>;
  setSubscriptionActive: () => void;
  shouldBlockRedirect?: boolean;
}): ReactElement => {
  const classes = useStyles();
  const { t } = useTranslation();

  const navigate = useNavigate();
  const navigateToReleaseFlow = useReleaseNavigation();

  const [searchParams] = useSearchParams();

  const [isOpen, setIsOpen] = useState(true);

  const {
    createNewRelease,
    data,
    isError,
    isLoading: releaseLoading,
  } = useCreateRelease({ hasUserAudio: false });

  useEffect(() => {
    if (!isError) {
      return;
    }
    navigate(locations.general.discovery.pathname);
  }, [isError, navigate]);

  useEffect(() => {
    if (!data?.id) {
      return;
    }
    navigateToReleaseFlow(data.id);
  }, [data, navigateToReleaseFlow]);

  useEffect(() => {
    userFetch();
  }, [userFetch]);

  useEffect(() => {
    // If call for '/users/me' endpoint fails then we should manually set
    // subscription status to 'active' inside user store to keep subscription flow
    if (userFetchError) {
      setSubscriptionActive();
    }
  }, [userFetchError, setSubscriptionActive]);

  const handleDistributionFlow = useCallback(() => {
    const songId = searchParams.get('song_id');
    if (songId) {
      createNewRelease({ songId });
      setIsOpen(false);
      return;
    }

    const releaseId = searchParams.get('release_id');
    if (releaseId) {
      navigate(locations.distribution.review.pathname(releaseId));
      return;
    }

    navigate(locations.releases.pathname);
  }, [navigate, createNewRelease, searchParams]);

  const handleDefaultFlow = useCallback(() => {
    if (shouldBlockRedirect) {
      setIsOpen(false);
      return;
    }

    navigate(locations.general.discovery.pathname);
  }, [navigate, shouldBlockRedirect]);

  const handleModalAction = useCallback(() => {
    if (searchParams.has('distribution_upsell_flow')) {
      handleDistributionFlow();
      return;
    }
    handleDefaultFlow();
  }, [searchParams, handleDistributionFlow, handleDefaultFlow]);

  return isLoading || releaseLoading ? (
    <div className={classes.loadingIndicator}>
      <CircularProgress color="primary" size="4rem" />
    </div>
  ) : (
    <Container maxWidth="sm">
      <ConversionDialog
        title={t('subscription:success.title')}
        state={{
          isOpen,
          error: null,
          isLoading: false,
        }}
        action={{
          onSubmit: handleModalAction,
          onClose: handleModalAction,
          onExited: () => null,
        }}
        description={t('subscription:success.message.1')}
        submitText={t('subscription:success.continue')}
      />
    </Container>
  );
};

export default SuccessSubscriptionModal;
